import {FormControl, FormField, Separator} from 'platform/components';
import {Box, Heading, HStack, Show, Space, VStack} from 'platform/foundation';

import {useEffect} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';
import {useLocation} from 'react-router-dom';

import {indexBy, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {SeriesType, useGetDefinitionTemplateListQuery, useGetSeriesListQuery} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {useGetServiceOrderVariantAvailableDocumentsQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  getOptionsFromSeries,
  resolveVariantTemplateId,
  SettingsFooter,
  SettingsSection,
} from '@dms/settings/shared';
import {useGetCurrentBranch, useTenant} from '@dms/shared';

import {Nullish, useNavigate} from 'shared/web';

import {DOCUMENT_SERIES_TYPES} from '../../constants/documentSeriesTypes';
import {FormType} from '../../types/ServiceOrderVariant';
import {getPrimaryTemplateIds} from '../../utils/getPrimaryTemplateIds';
import {getServiceOrderVariantsRoutes} from '../../utils/getServiceOrderVariantsRoutes';
import {getSoleSeriesId} from '../../utils/getSoleSeriesId';

interface DocumentsTabProps {
  control: FormControl<FormType>;
  formApi: UseFormReturn<FormType>;
  serviceOrderTypeCode?: string | null;
  serviceOrderVariantId?: string;
}

type TemplateOption = {label: string; value: string};
type GroupedOptions = {label?: string; options: TemplateOption[]};

type TemplateData = {
  options: GroupedOptions[];
  primaryTemplateId: string | undefined;
};

const ORDER_SHEET_CODES = [
  'workshop-order_list_commercial',
  'workshop-order_list_contract',
  'workshop-order_list_internal',
  'workshop-order_list_insurance',
  'workshop-order_list_warranty',
] as const;

// Repoints an id the order type no longer offers; it would render as an empty Choice and save back.
const resolveTemplateId = (data: TemplateData, currentTemplateId: string | Nullish) =>
  resolveVariantTemplateId(
    data.options.flatMap((group) => group.options).map((option) => ({id: option.value})),
    data.primaryTemplateId,
    currentTemplateId
  ) ?? data.primaryTemplateId;

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function DocumentsTab(props: DocumentsTabProps) {
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const serviceOrderVariantsRoutes = getServiceOrderVariantsRoutes(pathname);

  const isFastTrackEnabled = useWatch({
    control: props.control,
    name: 'documents.fastTrackIssue.isEnable',
  });

  const isUniqueOrderDocSeriesEnabled = useWatch({
    control: props.control,
    name: 'documents.order.isUniqueDocSeries',
  });

  const serviceOrderTypeId = useWatch({
    control: props.formApi.control,
    name: 'general.serviceOrderTypeId',
  });
  const serviceCaseTypeId = useWatch({
    control: props.formApi.control,
    name: 'general.serviceCaseTypeId',
  });

  const orderSheetTemplateIdValue = useWatch({
    control: props.formApi.control,
    name: 'documents.orderSheet.templateId',
  });
  const orderOfferTemplateIdValue = useWatch({
    control: props.formApi.control,
    name: 'documents.orderOffer.templateId',
  });
  const serviceOrderIssueTemplateIdValue = useWatch({
    control: props.formApi.control,
    name: 'documents.serviceOrderIssue.templateId',
  });
  const serviceOrderReturnTemplateIdValue = useWatch({
    control: props.formApi.control,
    name: 'documents.serviceOrderReturn.templateId',
  });

  // Get branch and tenant for authorization profiles query
  const {activeBranchId} = useGetCurrentBranch();
  const {tenant} = useTenant();

  const {data: availableDocuments, isLoading: isLoadingAvailableDocuments} =
    useGetServiceOrderVariantAvailableDocumentsQuery(
      props.serviceOrderVariantId
        ? {serviceOrderVariantId: props.serviceOrderVariantId}
        : {
            serviceOrderTypeId: serviceOrderTypeId ?? undefined,
            serviceCaseTypeId: serviceCaseTypeId ?? undefined,
          },
      {skip: !props.serviceOrderVariantId && isNilOrEmpty(serviceOrderTypeId)}
    );

  // Fetch definition template list to get primary template info
  const {data: definitionTemplateList} = useGetDefinitionTemplateListQuery({});

  // Matched by id and scoped to each template's own definition and profile, because the two
  // endpoints disagree on some documentCodes and a failed join scored every template non-primary.
  const primaryTemplateIds = getPrimaryTemplateIds(definitionTemplateList?.definitions);

  // Fetch authorization profiles to get names for group labels
  const {data: authorizationProfiles} = useGetAuthorizationProfilesQuery(
    {branchId: activeBranchId, 'x-tenant': tenant?.id ?? ''},
    {skip: isNil(tenant)}
  );

  // Create lookup map for authorization profiles by ID
  const getAuthorizationProfilesById = () =>
    authorizationProfiles ? indexBy((item) => item.id, authorizationProfiles) : {};

  const {data: series} = useGetSeriesListQuery({
    type: DOCUMENT_SERIES_TYPES,
  });

  /**
   * Get templates with grouping by authorization profile.
   * Returns grouped options for FormField and the primary template ID for prefilling.
   * Priority: templates with auth profile > default templates
   */
  const getTemplatesWithGrouping = (documentCodes: string | string[]): TemplateData => {
    if (!availableDocuments) {
      return {options: [], primaryTemplateId: undefined};
    }

    const authorizationProfilesById = getAuthorizationProfilesById();
    const codes = Array.isArray(documentCodes) ? documentCodes : [documentCodes];
    const allTemplates: Array<{
      id: string;
      title: string;
      documentCode: string;
      authorizationProfileId: string | null;
      isPrimary: boolean;
    }> = [];
    const seenIds = new Set<string>();

    for (const code of codes) {
      const doc = availableDocuments.find((d) => d.documentCode === code);
      if (doc) {
        for (const template of doc.templates) {
          // eslint-disable-next-line max-depth -- Pre-existing violation
          if (!seenIds.has(template.id)) {
            seenIds.add(template.id);
            allTemplates.push({
              id: template.id,
              title: template.title,
              documentCode: code,
              authorizationProfileId: template.authorizationProfileId,
              isPrimary: primaryTemplateIds.has(template.id),
            });
          }
        }
      }
    }

    // Separate templates by authorization profile
    const templatesWithAuth = allTemplates.filter((t) => isNotNilOrEmpty(t.authorizationProfileId));
    const defaultTemplates = allTemplates.filter((t) => isNilOrEmpty(t.authorizationProfileId));

    // Find primary template: first check templates with auth, then default
    // Sort by isPrimary so primary templates come first
    const sortedAuthTemplates = [...templatesWithAuth].sort(
      (a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0)
    );
    const sortedDefaultTemplates = [...defaultTemplates].sort(
      (a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0)
    );

    const primaryTemplate =
      sortedAuthTemplates.find((t) => t.isPrimary) ??
      sortedDefaultTemplates.find((t) => t.isPrimary) ??
      sortedAuthTemplates[0] ??
      sortedDefaultTemplates[0];

    // Build grouped options
    const options: GroupedOptions[] = [];

    // Group 1: Templates with authorization profile - use actual profile name
    if (isNotNilOrEmpty(sortedAuthTemplates)) {
      // Get unique authorization profile IDs from templates
      const authProfileIds = [
        ...new Set(sortedAuthTemplates.map((t) => t.authorizationProfileId).filter(Boolean)),
      ];

      // Group templates by authorization profile ID
      for (const profileId of authProfileIds) {
        const profileTemplates = sortedAuthTemplates.filter(
          (t) => t.authorizationProfileId === profileId
        );
        const profileName = authorizationProfilesById[profileId ?? '']?.name;

        options.push({
          label: profileName ?? i18n.t('general.labels.template'),
          options: profileTemplates.map((t) => ({label: t.title, value: t.id})),
        });
      }
    }

    // Group 2: Default templates (without authorization)
    if (isNotNilOrEmpty(sortedDefaultTemplates)) {
      options.push({
        label: i18n.t('entity.authorizationProfiles.labels.withoutAuthorization'),
        options: sortedDefaultTemplates.map((t) => ({label: t.title, value: t.id})),
      });
    }

    return {
      options,
      primaryTemplateId: primaryTemplate?.id,
    };
  };

  const getSeriesOptions = (type: SeriesType) =>
    series?.filter((s) => s.type === type).map((s) => ({label: s.name, value: s.id})) ?? [];

  const hasDocument = (documentCode: string) => {
    if (!availableDocuments) {
      return true;
    }
    return availableDocuments.some((doc) => doc.documentCode === documentCode);
  };

  const shouldShowOrderSheet =
    ORDER_SHEET_CODES.some((code) => hasDocument(code)) ||
    (!availableDocuments && !isLoadingAvailableDocuments);

  const shouldShowOrderOffer = hasDocument('workshop-service_order_offer-v2');
  const shouldShowHandlingProtocol = hasDocument('workshop-handling_protocol');
  const shouldShowResolutionProtocol = hasDocument('workshop-resolution_protocol');

  const orderSheetData = getTemplatesWithGrouping([...ORDER_SHEET_CODES]);
  const orderOfferData = getTemplatesWithGrouping('workshop-service_order_offer-v2');
  const handlingProtocolData = getTemplatesWithGrouping('workshop-handling_protocol');
  const resolutionProtocolData = getTemplatesWithGrouping('workshop-resolution_protocol');
  const serviceOrderIssueData = getTemplatesWithGrouping('warehouse-service-order-issue-html');
  const serviceOrderReturnData = getTemplatesWithGrouping('warehouse-service-order-return-html');
  const fastTrackData = getTemplatesWithGrouping('workshop-fast_track');

  useEffect(
    function prefillDocumentTemplates() {
      if (isLoadingAvailableDocuments || !availableDocuments || isNil(definitionTemplateList)) {
        return;
      }

      const prefillDocSeriesId = (
        name: 'documents.orderSheet.docSeriesId' | 'documents.orderOffer.docSeriesId',
        type: SeriesType,
        isVisible: boolean
      ) => {
        const soleSeriesId = getSoleSeriesId(series, type);

        if (isVisible && isNilOrEmpty(props.formApi.getValues(name)) && isNotNil(soleSeriesId)) {
          props.formApi.setValue(name, soleSeriesId, {shouldValidate: true});
        }
      };

      const prefillTemplateId = (
        name:
          | 'documents.orderSheet.templateId'
          | 'documents.orderOffer.templateId'
          | 'documents.orderHandlingProtocol.templateId'
          | 'documents.orderResolutionProtocol.templateId',
        data: TemplateData,
        isVisible: boolean
      ) => {
        const currentTemplateId = props.formApi.getValues(name);
        const resolvedTemplateId = resolveTemplateId(data, currentTemplateId);

        if (isVisible && isNotNil(resolvedTemplateId) && resolvedTemplateId !== currentTemplateId) {
          props.formApi.setValue(name, resolvedTemplateId, {shouldValidate: true});
        }
      };

      prefillTemplateId('documents.orderSheet.templateId', orderSheetData, shouldShowOrderSheet);

      prefillDocSeriesId(
        'documents.orderSheet.docSeriesId',
        'workshop/service_order_list',
        shouldShowOrderSheet
      );

      prefillTemplateId('documents.orderOffer.templateId', orderOfferData, shouldShowOrderOffer);

      prefillDocSeriesId(
        'documents.orderOffer.docSeriesId',
        'workshop/service_order_offer',
        shouldShowOrderOffer
      );

      prefillTemplateId(
        'documents.orderHandlingProtocol.templateId',
        handlingProtocolData,
        shouldShowHandlingProtocol
      );

      prefillTemplateId(
        'documents.orderResolutionProtocol.templateId',
        resolutionProtocolData,
        shouldShowResolutionProtocol
      );

      // Service Order Issue
      const serviceOrderIssueTemplateId = props.formApi.getValues(
        'documents.serviceOrderIssue.templateId'
      );
      if (isNilOrEmpty(serviceOrderIssueTemplateId)) {
        if (serviceOrderIssueData.primaryTemplateId) {
          props.formApi.setValue(
            'documents.serviceOrderIssue.templateId',
            serviceOrderIssueData.primaryTemplateId,
            {
              shouldValidate: true,
            }
          );
        }
      }

      // Service Order Return
      const serviceOrderReturnTemplateId = props.formApi.getValues(
        'documents.serviceOrderReturn.templateId'
      );
      if (isNilOrEmpty(serviceOrderReturnTemplateId)) {
        if (serviceOrderReturnData.primaryTemplateId) {
          props.formApi.setValue(
            'documents.serviceOrderReturn.templateId',
            serviceOrderReturnData.primaryTemplateId,
            {
              shouldValidate: true,
            }
          );
        }
      }

      // Fast Track Issue
      const fastTrackTemplateId = props.formApi.getValues('documents.fastTrackIssue.templateId');
      if (isFastTrackEnabled && isNilOrEmpty(fastTrackTemplateId)) {
        if (fastTrackData.primaryTemplateId) {
          props.formApi.setValue(
            'documents.fastTrackIssue.templateId',
            fastTrackData.primaryTemplateId,
            {
              shouldValidate: true,
            }
          );
        }
      }
    },
    // eslint-disable-next-line react/exhaustive-deps -- prefill runs on data arrival; adding formApi loops
    [
      availableDocuments,
      isLoadingAvailableDocuments,
      orderSheetData.primaryTemplateId,
      orderOfferData.primaryTemplateId,
      handlingProtocolData.primaryTemplateId,
      resolutionProtocolData.primaryTemplateId,
      serviceOrderIssueData.primaryTemplateId,
      serviceOrderReturnData.primaryTemplateId,
      fastTrackData.primaryTemplateId,
      series,
      definitionTemplateList,
    ]
  );

  // The prefill effect only fills fast track while it is already on, so switching it on fills here.
  const handleFastTrackToggle = (isEnabled: boolean) => {
    if (!isEnabled) {
      return;
    }

    const templateId = props.formApi.getValues('documents.fastTrackIssue.templateId');
    const soleSeriesId = getSoleSeriesId(series, 'warehouse/fast-track');

    if (isNilOrEmpty(templateId) && isNotNil(fastTrackData.primaryTemplateId)) {
      props.formApi.setValue(
        'documents.fastTrackIssue.templateId',
        fastTrackData.primaryTemplateId,
        {
          shouldValidate: true,
        }
      );
    }

    if (
      isNilOrEmpty(props.formApi.getValues('documents.fastTrackIssue.docSeriesId')) &&
      isNotNil(soleSeriesId)
    ) {
      props.formApi.setValue('documents.fastTrackIssue.docSeriesId', soleSeriesId, {
        shouldValidate: true,
      });
    }
  };

  return (
    <SettingsSection>
      <VStack spacing={4}>
        <HStack spacing={4}>
          <Box flex={1}>
            <Heading size={4}>{i18n.t('entity.document.labels.documentTemplates')}</Heading>
          </Box>
          <Box flex={1}>
            <Heading size={4}>{i18n.t('entity.document.labels.documentSerie')}</Heading>
          </Box>
        </HStack>
        <Separator spacing={0} />
        <Heading size={5}>{i18n.t('module.workshop.title')}</Heading>
        <Show when={shouldShowOrderSheet}>
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.orderSheet.templateId"
                type="choice"
                label={getTemplateLabel('entity.order.actions.orderList')}
                options={orderSheetData.options}
                isLoading={isLoadingAvailableDocuments}
                data-testid={testIds.settings.serviceOrderVariantsDetail('orderListTemplate')}
              />
            </Box>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.orderSheet.docSeriesId"
                type="choice"
                label={getSeriesLabel('entity.order.actions.orderList')}
                options={getSeriesOptions('workshop/service_order_list')}
                isDisabled={isNilOrEmpty(orderSheetTemplateIdValue)}
                data-testid={testIds.settings.serviceOrderVariantsDetail('orderListSeries')}
              />
            </Box>
          </HStack>
        </Show>
        <Show when={shouldShowOrderOffer}>
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.orderOffer.templateId"
                type="choice"
                label={getTemplateLabel('entity.interest.labels.offer')}
                options={orderOfferData.options}
                isLoading={isLoadingAvailableDocuments}
                data-testid={testIds.settings.serviceOrderVariantsDetail('offerTemplate')}
              />
            </Box>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.orderOffer.docSeriesId"
                type="choice"
                label={getSeriesLabel('entity.interest.labels.offer')}
                options={getSeriesOptions('workshop/service_order_offer')}
                isDisabled={isNilOrEmpty(orderOfferTemplateIdValue)}
                data-testid={testIds.settings.serviceOrderVariantsDetail('offerSeries')}
              />
            </Box>
          </HStack>
        </Show>
        <Show when={shouldShowHandlingProtocol}>
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.orderHandlingProtocol.templateId"
                type="choice"
                label={getTemplateLabel('entity.order.labels.handlingProtocol')}
                options={handlingProtocolData.options}
                isLoading={isLoadingAvailableDocuments}
                data-testid={testIds.settings.serviceOrderVariantsDetail(
                  'handlingProtocolTemplate'
                )}
              />
            </Box>
            <Box flex={1} />
          </HStack>
        </Show>
        <Show when={shouldShowResolutionProtocol}>
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.orderResolutionProtocol.templateId"
                type="choice"
                label={getTemplateLabel('entity.order.labels.resolutionProtocol')}
                options={resolutionProtocolData.options}
                isLoading={isLoadingAvailableDocuments}
                data-testid={testIds.settings.serviceOrderVariantsDetail(
                  'resolutionProtocolTemplate'
                )}
              />
            </Box>
            <Box flex={1} />
          </HStack>
        </Show>
        <HStack spacing={4}>
          <Box flex={1}>
            <FormField
              control={props.control}
              name="documents.order.isUniqueDocSeries"
              type="switch"
              label={i18n.t('entity.order.labels.enableUniqueServiceOrderDocSeries')}
              onChange={(isEnabled) => {
                if (!isEnabled) {
                  props.formApi.setValue('documents.order.docSeriesId', null, {
                    shouldValidate: true,
                  });
                }
              }}
              data-testid={testIds.settings.serviceOrderVariantsDetail(
                'enableUniqueServiceOrderDocSeries'
              )}
            />
          </Box>
        </HStack>
        <Show when={isUniqueOrderDocSeriesEnabled}>
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                isRequired
                control={props.control}
                name="documents.order.docSeriesId"
                type="choice"
                label={getSeriesLabel('entity.order.labels.serviceOrder')}
                options={getSeriesOptions('workshop/service_order')}
                data-testid={testIds.settings.serviceOrderVariantsDetail('serviceOrderDocSeries')}
              />
            </Box>
            <Box flex={1} />
          </HStack>
        </Show>
        <Separator spacing={0} />
        <Heading size={5}>{i18n.t('module.warehouse.title')}</Heading>
        <HStack spacing={4}>
          <Box flex={1}>
            <FormField
              control={props.control}
              name="documents.serviceOrderIssue.templateId"
              type="choice"
              label={getTemplateLabel('entity.order.labels.serviceOrderIssue')}
              options={serviceOrderIssueData.options}
              isLoading={isLoadingAvailableDocuments}
              data-testid={testIds.settings.serviceOrderVariantsDetail('serviceOrderIssueTemplate')}
            />
          </Box>
          <Box flex={1}>
            <FormField
              isRequired
              control={props.control}
              name="documents.serviceOrderIssue.docSeriesId"
              type="choice"
              label={getSeriesLabel('entity.order.labels.serviceOrderIssue')}
              options={getSeriesOptions('workshop/service_order_issue')}
              isDisabled={isNilOrEmpty(serviceOrderIssueTemplateIdValue)}
              data-testid={testIds.settings.serviceOrderVariantsDetail('serviceOrderIssueSeries')}
            />
          </Box>
        </HStack>
        <HStack spacing={4}>
          <Box flex={1}>
            <FormField
              control={props.control}
              name="documents.serviceOrderReturn.templateId"
              type="choice"
              label={getTemplateLabel('entity.order.labels.serviceOrderReturn')}
              options={serviceOrderReturnData.options}
              isLoading={isLoadingAvailableDocuments}
              data-testid={testIds.settings.serviceOrderVariantsDetail(
                'serviceOrderReturnTemplate'
              )}
            />
          </Box>
          <Box flex={1}>
            <FormField
              isRequired
              control={props.control}
              name="documents.serviceOrderReturn.docSeriesId"
              type="choice"
              label={getSeriesLabel('entity.order.labels.serviceOrderReturn')}
              options={getSeriesOptions('workshop/service_order_parts_return')}
              isDisabled={isNilOrEmpty(serviceOrderReturnTemplateIdValue)}
              data-testid={testIds.settings.serviceOrderVariantsDetail('serviceOrderReturnSeries')}
            />
          </Box>
        </HStack>
        <HStack spacing={4}>
          <Box flex={1}>
            <FormField
              control={props.control}
              name="documents.fastTrackIssue.isEnable"
              type="switch"
              label={i18n.t('entity.warehouse.labels.enableFastTrackIssue')}
              onChange={handleFastTrackToggle}
              data-testid={testIds.settings.serviceOrderVariantsDetail('enableFastTrackIssue')}
            />
          </Box>
        </HStack>
        <Show when={isFastTrackEnabled}>
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.fastTrackIssue.templateId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.fastTrackTemplate')}
                options={fastTrackData.options}
                data-testid={testIds.settings.serviceOrderVariantsDetail('fastTrackIssueTemplate')}
              />
            </Box>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="documents.fastTrackIssue.docSeriesId"
                type="choice"
                isRequired
                label={i18n.t('entity.warehouse.labels.fastTrackSeries')}
                options={getOptionsFromSeries(series, 'warehouse/fast-track')}
                data-testid={testIds.settings.serviceOrderVariantsDetail('fastTrackIssueSeries')}
              />
            </Box>
          </HStack>
        </Show>
      </VStack>
      <Space vertical={18} />
      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            title: i18n.t('general.actions.discardChanges'),
            onClick: () => navigate(serviceOrderVariantsRoutes.overview),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            variant: 'primary',
            title: i18n.t('general.actions.saveChanges'),
          },
        ]}
        data-testid={testIds.settings.serviceOrderVariantsDetail('footer')}
      />
    </SettingsSection>
  );
}

const getTemplateLabel = (key: string) =>
  `${i18n.t(key)} (${i18n.t('general.labels.template').toLocaleLowerCase()})`;

const getSeriesLabel = (key: string) =>
  `${i18n.t(key)} (${i18n.t('entity.document.labels.documentSerie').toLocaleLowerCase()})`;
